import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField,
  Button, IconButton, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';

const EASE = [0.22, 1, 0.36, 1];

const SubmitNominationModal = ({ open, onClose }) => {
  const [form, setForm]       = useState({ reddit_url: '', player_name: '', match: '' });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Clear field error on change
    if (errors[name]) setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const handleClose = () => {
    if (loading) return;
    setForm({ reddit_url: '', player_name: '', match: '' });
    setErrors({});
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      await api.post('/api/submissions/', form);
      setSuccess(true);
    } catch (err) {
      if (err.response?.data) {
        const data = err.response.data;
        // Server may return { detail: '...' } or { field: '...' } shaped errors
        if (data.detail) {
          setErrors({ _global: data.detail });
        } else {
          setErrors(data);
        }
      } else {
        setErrors({ _global: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(201,146,42,0.2)',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
        },
      }}
    >
      {/* Gold accent top bar */}
      <Box
        sx={{
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(201,146,42,0.65), transparent)',
        }}
      />

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 3.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontSize: '0.62rem', letterSpacing: '0.14em', display: 'block', mb: 0.4 }}
            >
              25/26 Season
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Submit a Nomination
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
            sx={{
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 0.6,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <AnimatePresence mode="wait">
          {success ? (
            /* ── Success state ──────────────────────────────────────────── */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ fontSize: '2.8rem', mb: 1.5 }}>🎭</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Nomination submitted!
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Thanks — we&apos;ll review it and add it to the voting pool if it meets our criteria.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{ borderRadius: 1.5, borderColor: 'divider' }}
                >
                  Close
                </Button>
              </Box>
            </motion.div>
          ) : (
            /* ── Form ───────────────────────────────────────────────────── */
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.65 }}>
                Spotted a dive worth nominating? Paste the Reddit post link below. It must be from{' '}
                <strong>r/soccer</strong> (or similar) and posted during the current season.
              </Typography>

              {errors._global && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                  {errors._global}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Reddit post link"
                  name="reddit_url"
                  value={form.reddit_url}
                  onChange={handleChange}
                  placeholder="https://www.reddit.com/r/soccer/comments/…"
                  error={!!errors.reddit_url}
                  helperText={errors.reddit_url || 'Must be a direct link to the Reddit post'}
                  fullWidth
                  required
                  autoComplete="off"
                  disabled={loading}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />

                <TextField
                  label="Player name"
                  name="player_name"
                  value={form.player_name}
                  onChange={handleChange}
                  placeholder="e.g. Jude Bellingham"
                  error={!!errors.player_name}
                  helperText={errors.player_name}
                  fullWidth
                  required
                  disabled={loading}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />

                <TextField
                  label="Match / description"
                  name="match"
                  value={form.match}
                  onChange={handleChange}
                  placeholder="e.g. Real Madrid vs Real Betis, La Liga"
                  error={!!errors.match}
                  helperText={errors.match}
                  fullWidth
                  required
                  disabled={loading}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    mt: 0.5,
                    bgcolor: 'primary.main',
                    color: '#111',
                    fontWeight: 700,
                    borderRadius: 1.5,
                    py: 1.2,
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                >
                  {loading ? 'Submitting…' : 'Submit nomination'}
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitNominationModal;
